// [QR-TABLES] Page publique client : scanne le QR → carte → commande → paiement → suivi. Aucun compte.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag, Banknote, Smartphone, Check, ChevronLeft, Clock, Loader2, Bell, Receipt } from "lucide-react";
import { API } from "../App";
import { PAYMENT_OPERATORS, getOperator } from "../lib/payments";
import { applySeo } from "../lib/seo";

const STEPS = ["en_attente", "servie", "encaissee"];
const STEP_LABELS = { en_attente: "Commande reçue", servie: "Servie", encaissee: "Payée" };

export default function TableClient() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cat, setCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [view, setView] = useState("menu"); // menu | pay | track
  const [payMethod, setPayMethod] = useState(null);
  const [operator, setOperator] = useState(null);
  const [sending, setSending] = useState(false);
  const [order, setOrder] = useState(null);
  const [addingTo, setAddingTo] = useState(false); // ajout à la commande ouverte (même addition)
  const [calls, setCalls] = useState({}); // type -> call en attente
  const storageKey = `table-order-${token}`;
  const isOpen = (o) => o && ["en_attente", "servie"].includes(o.status);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/table/${token}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "QR code invalide");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { applySeo({ title: data?.boutique?.name ? `${data.boutique.name} — ${data.table.name}` : "Commander", noindex: true }); }, [data]);

  // Restore an in-progress order and poll its status
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
    if (saved?.id) { setOrder(saved); setView("track"); }
  }, [storageKey]);
  useEffect(() => {
    if (!order?.id || order.status === "encaissee" || order.status === "annulee") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/public/table/${token}/order/${order.id}?s=${order.session_token || ""}`);
        setOrder((prev) => ({ ...prev, ...r.data }));
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, [order?.id, order?.status, order?.session_token, token]);

  const color = data?.boutique?.color || "#FF4500";
  const currency = data?.boutique?.currency || "F CFA";
  const fmt = (n) => `${Number(n).toLocaleString("fr-FR")} ${currency}`;
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const count = cart.reduce((s, i) => s + i.qty, 0);
  const operators = useMemo(() => PAYMENT_OPERATORS.filter((o) => (data?.payment_operators || []).includes(o.id)), [data]);
  const products = useMemo(() => (data?.products || []).filter((p) => cat === "all" || p.category_id === cat), [data, cat]);

  const add = (p) => {
    const v = p.variants.find((x) => x.available) || p.variants[0];
    setCart((prev) => {
      const k = `${p.id}-${v.id}`;
      const ex = prev.find((i) => i.key === k);
      return ex ? prev.map((i) => (i.key === k ? { ...i, qty: i.qty + 1 } : i)) : [...prev, { key: k, product_id: p.id, variant_id: v.id, name: p.name, price: p.price, qty: 1, size: v.size }];
    });
  };
  const change = (key, d) => setCart((prev) => prev.map((i) => (i.key === key ? { ...i, qty: i.qty + d } : i)).filter((i) => i.qty > 0));

  const submit = async () => {
    if (!payMethod) return toast.error("Choisissez comment vous réglez");
    if (payMethod === "card" && !operator) return toast.error("Choisissez votre opérateur");
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/table/${token}/order`, {
        items: cart.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, product_name: i.name, quantity: i.qty, unit_price: i.price, total: i.price * i.qty })),
        payment_method: payMethod, payment_operator: payMethod === "card" ? operator : null,
      });
      localStorage.setItem(storageKey, JSON.stringify(r.data));
      setOrder(r.data); setCart([]); setView("track");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible d'envoyer la commande"); }
    finally { setSending(false); }
  };

  const confirmExisting = async (o, method, op) => {
    try {
      await axios.post(`${API}/public/table/${token}/order/${o.id}/confirm`, { payment_method: method, payment_operator: op || null });
      toast.success("Merci ! La serveuse est prévenue.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // [APPEL-SERVEUSE]
  const callStaff = async (type) => {
    if (calls[type]) return;
    try {
      const r = await axios.post(`${API}/public/table/${token}/call`, { type });
      setCalls((c) => ({ ...c, [type]: r.data }));
      toast.success(type === "addition" ? "Addition demandée — la serveuse arrive" : "Serveuse prévenue — elle arrive");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => {
    const ids = Object.entries(calls).filter(([, c]) => c?.status === "en_attente");
    if (ids.length === 0) return;
    const t = setInterval(async () => {
      for (const [type, c] of ids) {
        try {
          const r = await axios.get(`${API}/public/table/${token}/call/${c.id}`);
          if (r.data.status !== "en_attente") { setCalls((prev) => { const n = { ...prev }; delete n[type]; return n; }); toast(type === "addition" ? "La serveuse arrive avec l'addition" : "La serveuse arrive !", { icon: "🏃" }); }
        } catch {}
      }
    }, 4000);
    return () => clearInterval(t);
  }, [calls, token]);

  const newOrder = () => { localStorage.removeItem(storageKey); setOrder(null); setAddingTo(false); setView("menu"); load(); };
  const startAdding = () => { setAddingTo(true); setCart([]); setView("menu"); };
  const q = () => `?s=${order?.session_token || ""}`;
  const saveOrder = (o) => { const merged = { ...order, ...o }; setOrder(merged); localStorage.setItem(storageKey, JSON.stringify(merged)); };

  const addToOrder = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/table/${token}/order/${order.id}/add${q()}`, {
        items: cart.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, product_name: i.name, quantity: i.qty, unit_price: i.price, total: i.price * i.qty })),
      });
      saveOrder(r.data); setCart([]); setAddingTo(false); setView("track");
      toast.success("Ajouté à votre commande");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible d'ajouter"); }
    finally { setSending(false); }
  };
  const changeLine = async (variantId, qty) => {
    try {
      const r = await axios.put(`${API}/public/table/${token}/order/${order.id}/items${q()}`, { quantities: { [variantId]: qty } });
      saveOrder(r.data);
      if (r.data.status === "annulee") toast("Commande annulée");
    } catch (e) { toast.error(e.response?.data?.detail || "Modification impossible"); }
  };
  const cancelOrder = async () => {
    try { const r = await axios.post(`${API}/public/table/${token}/order/${order.id}/cancel${q()}`); saveOrder({ status: r.data.status }); toast("Commande annulée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Annulation impossible"); }
  };
  const changePayment = async (method, op) => {
    try {
      await axios.post(`${API}/public/table/${token}/order/${order.id}/confirm`, { payment_method: method, payment_operator: op || null });
      saveOrder({ payment_method: method, payment_operator: method === "card" ? op : null, client_confirmed: true });
      toast.success("Mode de paiement mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (error) return <Shell color={color}><p className="text-center text-white/80 mt-20" data-testid="table-error">{error}</p></Shell>;
  if (!data) return <Shell color={color}><Loader2 className="w-8 h-8 animate-spin mx-auto mt-24 text-white/70" /></Shell>;

  return (
    <Shell color={color}>
      {/* Header */}
      <header className="px-5 pt-6 pb-4 flex items-center gap-3" data-testid="table-header">
        {data.boutique.logo ? <img src={data.boutique.logo} alt="" className="w-11 h-11 rounded-2xl object-cover bg-white/10" /> : <div className="w-11 h-11 rounded-2xl bg-white/10" />}
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-white leading-tight truncate">{data.boutique.name}</h1>
          <p className="text-xs text-white/60">{data.table.name} · Commandez depuis votre téléphone</p>
        </div>
      </header>

      {view !== "pay" && (
        <div className="px-4 pb-3 grid grid-cols-2 gap-2" data-testid="call-bar">
          <button onClick={() => callStaff("serveuse")} disabled={!!calls.serveuse} className="py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border border-white/15 bg-white/[0.06] text-white disabled:opacity-100" style={calls.serveuse ? { borderColor: color, color } : {}} data-testid="call-waiter-btn">
            <Bell className="w-4 h-4" /> {calls.serveuse ? "Serveuse prévenue ✓" : "Appeler la serveuse"}
          </button>
          <button onClick={() => callStaff("addition")} disabled={!!calls.addition} className="py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border border-white/15 bg-white/[0.06] text-white disabled:opacity-100" style={calls.addition ? { borderColor: color, color } : {}} data-testid="call-bill-btn">
            <Receipt className="w-4 h-4" /> {calls.addition ? "Addition demandée ✓" : "Demander l'addition"}
          </button>
        </div>
      )}

      {view === "track" && order && (
        <Track order={order} color={color} fmt={fmt} onNew={newOrder} onAdd={startAdding} onChangeLine={changeLine} onCancel={cancelOrder} onChangePayment={changePayment} operators={operators} />
      )}

      {view === "menu" && (
        <>
          {addingTo && order && (
            <div className="mx-4 mb-3 p-3 rounded-2xl border flex items-center gap-3" style={{ borderColor: color, background: `${color}18` }} data-testid="adding-banner">
              <Plus className="w-4 h-4" style={{ color }} />
              <p className="text-sm text-white flex-1">Ajout à votre commande · <b>{fmt(order.total)}</b> déjà commandés</p>
              <button onClick={() => { setAddingTo(false); setCart([]); setView("track"); }} className="text-xs font-semibold text-white px-2.5 py-1 rounded-lg bg-black/40 border border-white/20" data-testid="adding-back-btn">Retour</button>
            </div>
          )}
          {/* Commandes déjà en cours à cette table (prises par la serveuse) */}
          {data.open_orders.filter((o) => !o.client_confirmed && o.id !== order?.id).map((o) => (
            <div key={o.id} className="mx-4 mb-3 p-4 rounded-2xl bg-white/10 border border-white/15" data-testid={`open-order-${o.id}`}>
              <p className="text-xs text-white/60 mb-1">Commande prise par la serveuse</p>
              <p className="text-white font-semibold">{o.items.map((i) => `${i.quantity}× ${i.product_name}`).join(", ")}</p>
              <p className="text-white text-lg font-bold mt-1">{fmt(o.total)}</p>
              <p className="text-xs text-white/70 mt-2">Comment réglez-vous ?</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button onClick={() => confirmExisting(o, "cash")} className="py-3 rounded-xl bg-black text-white text-sm font-semibold flex items-center justify-center gap-2" data-testid={`confirm-cash-${o.id}`}><Banknote className="w-4 h-4" /> Espèces</button>
                <OperatorMenu operators={operators} onPick={(op) => confirmExisting(o, "card", op)} />
              </div>
            </div>
          ))}
          {!addingTo && data.open_orders.filter((o) => o.client_confirmed && o.id !== order?.id).map((o) => (
            <button key={o.id} onClick={() => { setOrder(o); setView("track"); }} className="mx-4 mb-3 p-3 rounded-2xl bg-white/10 border border-white/15 w-[calc(100%-2rem)] text-left" data-testid={`track-order-${o.id}`}>
              <p className="text-xs text-white/60 flex items-center gap-1"><Clock className="w-3 h-3" /> Commande en cours · {fmt(o.total)} · {STEP_LABELS[o.status]}</p>
            </button>
          ))}

          {/* Catégories */}
          <div className="flex gap-2 px-4 overflow-x-auto pb-3 no-scrollbar" data-testid="category-tabs">
            <Tab active={cat === "all"} onClick={() => setCat("all")} color={color}>Tout</Tab>
            {data.categories.map((c) => <Tab key={c.id} active={cat === c.id} onClick={() => setCat(c.id)} color={color}>{c.icon} {c.name}</Tab>)}
          </div>

          {/* Articles */}
          <div className="grid grid-cols-2 gap-3 px-4 pb-32">
            {products.map((p) => {
              const inCart = cart.filter((i) => i.product_id === p.id).reduce((s, i) => s + i.qty, 0);
              return (
                <button key={p.id} onClick={() => add(p)} className="rounded-2xl overflow-hidden bg-white/[0.07] border border-white/10 text-left active:scale-95 transition-transform" data-testid={`menu-item-${p.id}`}>
                  <div className="aspect-[4/3] bg-white/5 flex items-center justify-center text-4xl relative">
                    {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <span>{p.category_icon || "🍽️"}</span>}
                    {inCart > 0 && <span className="absolute top-2 right-2 w-7 h-7 rounded-full text-white text-sm font-bold flex items-center justify-center" style={{ background: color }} data-testid={`menu-count-${p.id}`}>{inCart}</span>}
                  </div>
                  <div className="p-3">
                    <p className="text-white text-sm font-semibold leading-tight line-clamp-2">{p.name}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-white/90 text-sm font-bold">{fmt(p.price)}</span>
                      <span className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ background: color }}><Plus className="w-4 h-4" /></span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Panier collant */}
          {count > 0 && (
            <div className="fixed bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black via-black/95 to-transparent">
              <button onClick={() => (addingTo ? addToOrder() : setView("pay"))} disabled={sending} className="w-full py-4 rounded-2xl text-white font-bold text-base flex items-center justify-between px-5 shadow-2xl" style={{ background: color }} data-testid="go-to-pay-btn">
                <span className="flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> {count} article{count > 1 ? "s" : ""}</span>
                <span>{sending ? "…" : addingTo ? `+ ${fmt(total)} · Ajouter à ma commande` : `${fmt(total)} · Commander`}</span>
              </button>
            </div>
          )}
        </>
      )}

      {view === "pay" && (
        <div className="px-4 pb-10" data-testid="pay-view">
          <button onClick={() => setView("menu")} className="text-white/70 text-sm flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" /> Retour à la carte</button>
          <div className="rounded-2xl bg-white/[0.07] border border-white/10 p-4 space-y-2">
            {cart.map((i) => (
              <div key={i.key} className="flex items-center gap-2 text-white text-sm" data-testid={`cart-line-${i.key}`}>
                <span className="flex-1 truncate">{i.name}</span>
                <button onClick={() => change(i.key, -1)} className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                <span className="w-5 text-center font-semibold">{i.qty}</span>
                <button onClick={() => change(i.key, 1)} className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                <span className="w-24 text-right font-semibold">{fmt(i.price * i.qty)}</span>
              </div>
            ))}
            <div className="flex justify-between text-white font-bold text-lg border-t border-white/10 pt-3"><span>Total</span><span data-testid="pay-total">{fmt(total)}</span></div>
          </div>

          <p className="text-white/80 text-sm font-semibold mt-6 mb-2">Comment réglez-vous ?</p>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => { setPayMethod("cash"); setOperator(null); }} className="py-5 rounded-2xl font-bold text-white flex flex-col items-center gap-2 border-2" style={{ background: "#111", borderColor: payMethod === "cash" ? color : "#333" }} data-testid="client-pay-cash">
              <Banknote className="w-6 h-6" /> Espèces {payMethod === "cash" && <Check className="w-4 h-4" style={{ color }} />}
            </button>
            <button onClick={() => setPayMethod("card")} className="py-5 rounded-2xl font-bold flex flex-col items-center gap-2 border-2" style={{ background: payMethod === "card" ? color : "#22C55E", borderColor: payMethod === "card" ? color : "#22C55E", color: "#fff" }} data-testid="client-pay-card">
              <Smartphone className="w-6 h-6" /> Carte / Mobile Money
            </button>
          </div>
          {payMethod === "card" && (
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid="client-operators">
              {operators.map((o) => (
                <button key={o.id} onClick={() => setOperator(o.id)} className="py-3 rounded-xl text-xs font-bold border-2" style={{ background: operator === o.id ? color : o.color, color: operator === o.id ? "#fff" : o.text, borderColor: operator === o.id ? "#fff" : o.color }} data-testid={`client-operator-${o.id}`}>{o.short}</button>
              ))}
            </div>
          )}
          {payMethod === "cash" && <p className="text-white/70 text-xs mt-3 text-center">Vous réglez en espèces à la serveuse ou au comptoir.</p>}
          {payMethod === "card" && operator && <p className="text-white/70 text-xs mt-3 text-center">Réglez au comptoir ou attendez la serveuse avec {getOperator(operator)?.label}.</p>}

          <button onClick={submit} disabled={sending || !payMethod || (payMethod === "card" && !operator)} className="w-full mt-6 py-4 rounded-2xl text-white font-bold text-base disabled:opacity-40" style={{ background: color }} data-testid="client-confirm-order-btn">
            {sending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Je confirme ma commande"}
          </button>
        </div>
      )}
    </Shell>
  );
}

function Shell({ color, children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" style={{ "--tc": color }}>
      <div className="max-w-md mx-auto min-h-screen relative">{children}</div>
    </div>
  );
}

function Tab({ active, onClick, color, children }) {
  return (
    <button onClick={onClick} className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border" style={{ background: active ? color : "transparent", borderColor: active ? color : "rgba(255,255,255,0.15)", color: active ? "#fff" : "rgba(255,255,255,0.7)" }}>{children}</button>
  );
}

function OperatorMenu({ operators, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="w-full py-3 rounded-xl bg-[#22C55E] text-white text-sm font-semibold flex items-center justify-center gap-2" data-testid="confirm-card-menu"><Smartphone className="w-4 h-4" /> Mobile Money</button>
      {open && (
        <div className="absolute z-10 mt-1 left-0 right-0 grid grid-cols-2 gap-1 p-2 rounded-xl bg-[#151515] border border-white/15">
          {operators.map((o) => <button key={o.id} onClick={() => { setOpen(false); onPick(o.id); }} className="py-2 rounded-lg text-xs font-bold" style={{ background: o.color, color: o.text }} data-testid={`confirm-operator-${o.id}`}>{o.short}</button>)}
        </div>
      )}
    </div>
  );
}

function Track({ order, color, fmt, onNew, onAdd, onChangeLine, onCancel, onChangePayment, operators }) {
  const [editPay, setEditPay] = useState(false);
  const [payMethod, setPayMethod] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const idx = STEPS.indexOf(order.status);
  const cancelled = order.status === "annulee";
  const open = ["en_attente", "servie"].includes(order.status);
  const op = getOperator(order.payment_operator);
  const anyLocked = (order.items || []).some((i) => i.locked);
  return (
    <div className="px-4 pb-10" data-testid="track-view">
      <div className="rounded-3xl bg-white/[0.07] border border-white/10 p-5 text-center">
        <p className="text-white/60 text-xs uppercase tracking-widest">{order.table_name}</p>
        <p className="text-white text-3xl font-black mt-1" data-testid="track-total">{fmt(order.total)}</p>
        <p className="text-white/80 text-sm mt-1">{order.payment_method === "cash" ? "Règlement en espèces" : `Règlement ${op?.label || "Mobile Money"}`}
          {open && <button onClick={() => setEditPay(!editPay)} className="ml-2 text-xs underline text-white/60" data-testid="change-payment-btn">changer</button>}
        </p>
        {editPay && open && (
          <div className="mt-3 space-y-2" data-testid="change-payment-panel">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => { onChangePayment("cash"); setEditPay(false); setPayMethod(null); }} className="py-2.5 rounded-xl bg-black text-white text-xs font-bold border border-white/20" data-testid="change-pay-cash">Espèces</button>
              <button onClick={() => setPayMethod("card")} className="py-2.5 rounded-xl text-white text-xs font-bold" style={{ background: payMethod === "card" ? color : "#22C55E" }} data-testid="change-pay-card">Mobile Money</button>
            </div>
            {payMethod === "card" && <div className="grid grid-cols-3 gap-1.5">{operators.map((o) => <button key={o.id} onClick={() => { onChangePayment("card", o.id); setEditPay(false); setPayMethod(null); }} className="py-2 rounded-lg text-[11px] font-bold" style={{ background: o.color, color: o.text }} data-testid={`change-operator-${o.id}`}>{o.short}</button>)}</div>}
          </div>
        )}
        <div className="flex items-center justify-center gap-3 mt-3 text-xs">
          <span className={`px-2.5 py-1 rounded-full ${order.client_confirmed ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/50"}`} data-testid="track-client-confirmed">✓ Vous</span>
          <span className={`px-2.5 py-1 rounded-full ${order.staff_confirmed ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/50"}`} data-testid="track-staff-confirmed">{order.staff_confirmed ? "✓ Serveuse" : "⏳ Serveuse"}</span>
        </div>
      </div>

      {cancelled ? (
        <p className="text-center text-red-300 mt-6" data-testid="track-cancelled">Commande annulée.</p>
      ) : (
        <div className="mt-6 space-y-3" data-testid="track-steps">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: i <= idx ? color : "rgba(255,255,255,0.1)", color: i <= idx ? "#fff" : "rgba(255,255,255,0.4)" }}>{i < idx || (i === idx && idx === STEPS.length - 1) ? <Check className="w-4 h-4" /> : i + 1}</div>
              <span className={`text-sm ${i <= idx ? "text-white font-semibold" : "text-white/40"}`}>{STEP_LABELS[s]}{i === idx && idx < 2 ? " …" : ""}</span>
            </div>
          ))}
        </div>
      )}

      {/* Lignes : modifiables tant que non servies */}
      <div className="mt-6 rounded-2xl bg-white/[0.05] p-4 space-y-2" data-testid="track-items">
        {anyLocked && open && <p className="text-[11px] text-amber-300 flex items-center gap-1 mb-1" data-testid="locked-banner"><Clock className="w-3 h-3" /> Déjà en préparation — pour un changement, demandez à la serveuse.</p>}
        {order.items.map((i, k) => (
          <div key={k} className="flex items-center gap-2 text-sm text-white/85" data-testid={`track-line-${i.variant_id}`}>
            <span className="flex-1 truncate">{i.product_name}{i.locked && <span className="ml-1 text-white/40">🔒</span>}</span>
            {open && !i.locked ? (
              <>
                <button onClick={() => onChangeLine(i.variant_id, i.quantity - 1)} className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center" data-testid={`line-minus-${i.variant_id}`}><Minus className="w-3.5 h-3.5" /></button>
                <span className="w-5 text-center font-semibold" data-testid={`line-qty-${i.variant_id}`}>{i.quantity}</span>
                <button onClick={() => onChangeLine(i.variant_id, i.quantity + 1)} className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center" data-testid={`line-plus-${i.variant_id}`}><Plus className="w-3.5 h-3.5" /></button>
              </>
            ) : <span className="w-10 text-center text-white/60">×{i.quantity}</span>}
            <span className="w-24 text-right">{fmt(i.total)}</span>
          </div>
        ))}
      </div>

      {open && (
        <button onClick={onAdd} className="w-full mt-4 py-4 rounded-2xl text-white font-bold flex items-center justify-center gap-2" style={{ background: color }} data-testid="add-more-btn"><Plus className="w-5 h-5" /> Ajouter des boissons / articles</button>
      )}
      {open && !anyLocked && (
        !confirmCancel
          ? <button onClick={() => setConfirmCancel(true)} className="w-full mt-3 py-3 rounded-2xl text-white/60 text-sm border border-white/15" data-testid="client-cancel-btn">Annuler ma commande</button>
          : <div className="flex gap-2 mt-3"><button onClick={onCancel} className="flex-1 py-3 rounded-2xl bg-red-500/80 text-white text-sm font-semibold" data-testid="client-cancel-confirm">Oui, annuler</button><button onClick={() => setConfirmCancel(false)} className="flex-1 py-3 rounded-2xl border border-white/15 text-white/70 text-sm">Non</button></div>
      )}
      {(order.status === "encaissee" || cancelled) && (
        <button onClick={onNew} className="w-full mt-6 py-4 rounded-2xl text-white font-bold" style={{ background: color }} data-testid="new-order-btn">Nouvelle commande</button>
      )}
    </div>
  );
}
